import { FC, useCallback } from "react";
import { Stack } from "@mui/material";
import { useToggle } from "rooks";

import { ProviderType, RepresentsACostTrend } from "@/services/cloudchipr.api";
import { ExplainWithAiAnomalyTrendItemWrapper } from "@/components/common/explain-with-ai-drawer/components/body/components/trend/ExplainWithAiAnomalyTrendItemWrapper";
import { ExplainWithAiAnomalyTrendItemHeaderTitle } from "@/components/common/explain-with-ai-drawer/components/body/components/trend/ExplainWithAiAnomalyTrendItemHeaderTitle";

import { ExplainWithAiLoading } from "@/components/common/explain-with-ai-drawer/components/common/ExplainWithAiLoading";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getExplainWithAiAnomalyByIdThunk } from "@/store/explain-with-ai/thunks/getExplainWithAiAnomalyByIdThunk";
import { explainWithAiAnomalySummaryDataByIdSelector } from "@/store/explain-with-ai/selectors/data/anomaly-data/explainWithAiAnomalySummaryDataByIdSelector";
import { explainWithAiAnomalyByIdLoadingSelector } from "@/store/explain-with-ai/selectors/data/anomaly-data/explainWithAiAnomalyByIdLoadingSelector";
import { anomaliesHtmlStyle } from "@/components/common/explain-with-ai-drawer/components/body/components/utils/constants";

interface ExplainWithAiAnomalyTrendItemProps extends RepresentsACostTrend {
  provider: ProviderType;
  type: "increase" | "decrease";
}
export const ExplainWithAiAnomalyTrendItem: FC<
  ExplainWithAiAnomalyTrendItemProps
> = ({ id, ...props }) => {
  const [collapsed, toggleCollapsed] = useToggle(true);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) =>
    explainWithAiAnomalyByIdLoadingSelector(state, id),
  );
  const data = useAppSelector((state) =>
    explainWithAiAnomalySummaryDataByIdSelector(state, id),
  );

  const onCollapse = useCallback(() => {
    toggleCollapsed();
    dispatch(getExplainWithAiAnomalyByIdThunk(id));
  }, [toggleCollapsed, id, dispatch]);

  return (
    <ExplainWithAiAnomalyTrendItemWrapper
      headerTitle={
        <ExplainWithAiAnomalyTrendItemHeaderTitle id={id} {...props} />
      }
      collapsed={collapsed}
      onCollapse={onCollapse}
      loading={loading}
    >
      {loading ? (
        <Stack p={1} pl={2}>
          <ExplainWithAiLoading variant="dark" />
        </Stack>
      ) : (
        <Stack
          overflow="auto"
          px={4}
          dangerouslySetInnerHTML={{ __html: data ?? "" }}
          sx={anomaliesHtmlStyle}
        />
      )}
    </ExplainWithAiAnomalyTrendItemWrapper>
  );
};
